import {
  operatorNeedsValue,
  parseWithinDaysValue,
  WITHIN_DAYS_OPERATOR,
} from '../components/articleSearchConfigs';
import type { ActiveFilter, FilterBarConfig, FilterBarState } from '../types';

/**
 * Pure helpers for the /search page: keeping the search box, the filter bar,
 * and the URL query string in sync.
 *
 * URL shape: ?q=free+text&f=domain:contains:willison&f=tier:>=:4&sort=published_at&dir=asc
 * `sort`/`dir` are omitted when they match the default state, so a bare
 * /search URL stays clean. Legacy ?domain=example.com links still work.
 */

export function textFilterValue(state: FilterBarState): string {
  const filter = state.filters.find((f) => f.field === 'text' && f.operator === 'contains');
  return filter?.value ?? '';
}

/** Set, replace, or remove the free-text filter while preserving all other filters. */
export function withTextFilter(state: FilterBarState, value: string): FilterBarState {
  const others = state.filters.filter((f) => !(f.field === 'text' && f.operator === 'contains'));
  if (!value) {
    return { ...state, filters: others };
  }
  const existing = state.filters.find((f) => f.field === 'text' && f.operator === 'contains');
  const textFilter: ActiveFilter = existing
    ? { ...existing, value }
    : { id: crypto.randomUUID(), field: 'text', operator: 'contains', value };
  return { ...state, filters: [textFilter, ...others] };
}

export function filterStateToParams(
  state: FilterBarState,
  defaultState: FilterBarState,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const f of state.filters) {
    if (f.value === '' && operatorNeedsValue(f.operator)) continue; // incomplete row
    if (f.field === 'text' && f.operator === 'contains') {
      params.set('q', f.value);
    } else {
      params.append('f', `${f.field}:${f.operator}:${f.value}`);
    }
  }
  if (state.orderBy !== defaultState.orderBy || state.orderDir !== defaultState.orderDir) {
    params.set('sort', state.orderBy);
    params.set('dir', state.orderDir);
  }
  return params;
}

/** Parse search state from the URL. Returns null when the URL carries none. */
export function paramsToFilterState(
  params: URLSearchParams,
  defaultState: FilterBarState,
  makeId: () => string = () => crypto.randomUUID(),
): FilterBarState | null {
  const filters: ActiveFilter[] = [];
  const q = params.get('q');
  if (q) {
    filters.push({ id: makeId(), field: 'text', operator: 'contains', value: q });
  }
  const legacyDomain = params.get('domain');
  if (legacyDomain) {
    filters.push({ id: makeId(), field: 'domain', operator: 'equals', value: legacyDomain });
  }
  for (const raw of params.getAll('f')) {
    const [field, operator, ...rest] = raw.split(':');
    if (!field || !operator) continue;
    filters.push({ id: makeId(), field, operator, value: rest.join(':') });
  }
  const sort = params.get('sort');
  const dir = params.get('dir');
  if (filters.length === 0 && !sort && !dir) return null;
  return {
    filters,
    orderBy: sort || defaultState.orderBy,
    orderDir: dir === 'asc' || dir === 'desc' ? dir : defaultState.orderDir,
  };
}

/** Human-readable one-line description of what the current search matches. */
export function describeSearch(state: FilterBarState, config: FilterBarConfig): string {
  const parts: string[] = [];
  for (const f of state.filters) {
    if (f.value === '' && operatorNeedsValue(f.operator)) continue;
    const field = config.fields.find((opt) => opt.value === f.field);
    const fieldLabel = field?.label ?? f.field;
    if (f.operator === WITHIN_DAYS_OPERATOR) {
      // Composite `<date>,<days>` value; spell it out instead of quoting it raw.
      const { date, days } = parseWithinDaysValue(f.value);
      parts.push(`${fieldLabel} within ${days} day${days === '1' ? '' : 's'} of ${date}`);
      continue;
    }
    const operatorLabel =
      field?.operators?.find((op) => op.value === f.operator)?.label ?? f.operator;
    parts.push(operatorNeedsValue(f.operator)
      ? `${fieldLabel} ${operatorLabel} “${f.value}”`
      : `${fieldLabel} ${operatorLabel}`);
  }
  if (parts.length === 0) parts.push('All articles');
  const sortOption = config.sortOptions.find((opt) => opt.value === state.orderBy);
  const sortLabel = sortOption?.label ?? state.orderBy;
  const dirLabel = sortOption?.dirCategory === 'value'
    ? (state.orderDir === 'desc' ? 'highest first' : 'lowest first')
    : (state.orderDir === 'desc' ? 'newest first' : 'oldest first');
  return `${parts.join(' · ')} — sorted by ${sortLabel}, ${dirLabel}`;
}
